/**
 * WebSocket client module — manages the real-time connection to the gateway
 * server. Provides event-driven pub/sub with automatic reconnection using
 * exponential backoff (1s → 30s max).
 *
 * Usage:
 *   const ws = new WSClient('ws://localhost:8080/ws');
 *   ws.on('telemetry.update', (event) => { ... });
 *   ws.on('*', (event) => { ... });  // wildcard listener
 *   ws.connect();
 * @module ws/client
 */
import type { WSEvent } from "../types/telemetry.ts";

/** Callback signature for WebSocket event listeners. */
type EventCallback = (event: WSEvent) => void;

/**
 * WebSocket client with auto-reconnect and event-based message routing.
 * Maintains a listener map of event type → callback set.
 */
export class WSClient {
	private ws: WebSocket | null = null;
	private url: string;
	private listeners: Map<string, Set<EventCallback>> = new Map();
	private reconnectDelay = 1000;
	private maxReconnectDelay = 30000;
	private shouldReconnect = true;
	private pingTimer: ReturnType<typeof setInterval> | null = null;

	constructor(url: string) {
		this.url = url;
	}

	/** Start the WebSocket connection. Will auto-reconnect on disconnect. */
	connect(): void {
		this.shouldReconnect = true;
		this.doConnect();
	}

	private doConnect(): void {
		this.ws = new WebSocket(this.url);

		this.ws.onopen = () => {
			this.reconnectDelay = 1000;
			this.updateStatus(true);
			this.startPing();
		};

		this.ws.onmessage = (event) => {
			try {
				const wsEvent: WSEvent = JSON.parse(event.data);
				// Silently drop server pong responses — they only serve to keep the
				// connection alive and carry no application data.
				if (wsEvent.type === "pong") return;
				this.dispatch(wsEvent);
			} catch {
				console.warn("ws: failed to parse message", event.data);
			}
		};

		this.ws.onclose = () => {
			this.stopPing();
			this.updateStatus(false);
			if (this.shouldReconnect) {
				setTimeout(() => this.doConnect(), this.reconnectDelay);
				this.reconnectDelay = Math.min(
					this.reconnectDelay * 2,
					this.maxReconnectDelay,
				);
			}
		};

		this.ws.onerror = () => {
			this.ws?.close();
		};
	}

	private startPing(): void {
		this.pingTimer = setInterval(() => {
			if (this.ws?.readyState === WebSocket.OPEN) {
				this.ws.send(JSON.stringify({ type: "ping" }));
			}
		}, 30_000);
	}

	private stopPing(): void {
		if (this.pingTimer !== null) {
			clearInterval(this.pingTimer);
			this.pingTimer = null;
		}
	}

	/**
	 * Register a callback for a specific event type.
	 * Use '*' for a wildcard that receives all events.
	 * @returns An unsubscribe function.
	 */
	on(eventType: string, callback: EventCallback): () => void {
		if (!this.listeners.has(eventType)) {
			this.listeners.set(eventType, new Set());
		}
		this.listeners.get(eventType)?.add(callback);

		// Return unsubscribe function
		return () => {
			this.listeners.get(eventType)?.delete(callback);
		};
	}

	/** Dispatch a received event to matching and wildcard listeners. */
	private dispatch(event: WSEvent): void {
		// Notify specific listeners
		this.listeners.get(event.type)?.forEach((cb) => {
			cb(event);
		});
		// Notify wildcard listeners
		this.listeners.get("*")?.forEach((cb) => {
			cb(event);
		});
	}

	/** Update the connection status indicator in the DOM. */
	private updateStatus(connected: boolean): void {
		const el = document.getElementById("connection-status");
		if (el) {
			el.className = connected ? "status-connected" : "status-disconnected";
			const label = el.querySelector<HTMLElement>(".status-label");
			if (label) label.textContent = connected ? "Connected" : "Disconnected";
		}
	}

	/** Gracefully close the WebSocket connection (no auto-reconnect). */
	disconnect(): void {
		this.shouldReconnect = false;
		this.stopPing();
		this.ws?.close();
	}
}
